import { Button } from '@akin-home/buttons/native';
import { ChatResponseOption } from '@akin-home/chat';
import { Text } from '@akin-home/text/native';
import { colors } from '@akin-home/theme';
import { useBool } from '@akin-home/utils/react';
import { Portal } from '@gorhom/portal';
import { useState } from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';
import { ChatTextItem } from '../../../ChatItem/types';
import { DisplayEmotionPickerCommand } from '../../../commands/types';
import { useFocusedChatState } from '../../hooks/useFocusedChatState';
import { ChatItemTextBlock } from './ChatItemText';
import { EmotionPickerHelpButton } from './EmotionPickerHelpButton';
import { EmotionPickerWheel } from './EmotionPickerWheel';
import { OptionContainer } from './Options';

type Props = {
  item: ChatTextItem;
  index: number;
  onPressOption?(
    option: ChatResponseOption,
    index: number,
    context?: any
  ): void;
};

export function ChatItemEmotionPicker(props: Props) {
  const { item, index } = props;

  const command: DisplayEmotionPickerCommand = item.commands?.find(
    (cmd) => cmd.type === 'display_emotion_picker'
  );

  const [image_zoomed, showImageZoom, hideImageZoom] = useBool(true);

  const onSelectOtherEmotion = useFocusedChatState(
    (state) => state.onPressOtherOption
  );

  const {
    data: { text, options, image_url, multiple_choice_options },
    commands,
  } = item;

  const multiselect_command = commands?.find(
    (command) => command.type === 'multiselect_actions'
  );

  const onPressDoneMultiselect = useFocusedChatState(
    (state) => state.doneMultiselect
  );
  const onPressSkipMultiselect = useFocusedChatState(
    (state) => state.skipMultiselect
  );

  const [selections, setSelections] = useState<{ [x: string]: string }>({});

  const onPressOption = (option: ChatResponseOption, value = '') => {
    if (!multiple_choice_options) {
      hideImageZoom();
      props.onPressOption?.(option, index, { geneva_picker_value: value });
    } else {
      const existingSelection = selections[option.input.value];

      // Tracks whether we should be toggling the selected field.
      let toggleSelected = false;

      if (existingSelection === undefined) {
        // Option has not been selected before
        setSelections({ ...selections, [option.input.value]: value });
        toggleSelected = true;
      } else if (existingSelection === value || value === '') {
        // Remove already selected option
        const { [option.input.value]: _, ...remainingSelections } = selections;
        setSelections(remainingSelections);
        toggleSelected = true;
      } else {
        // Update old selection with new value
        setSelections({ ...selections, [option.input.value]: value });
      }

      if (toggleSelected) {
        props.onPressOption?.(option, index);
      }
    }
  };

  const onPressOtherOption = () => {
    onSelectOtherEmotion(command.data.other_emotion_input || 'Other', index);
    hideImageZoom();
  };

  const onDone = () => {
    hideImageZoom();
    const done_message = multiselect_command.data.next;
    if (done_message) {
      onPressDoneMultiselect?.(done_message, index, {
        geneva_values_json: JSON.stringify(selections),
      });
    }
  };

  const onSkip = () => {
    hideImageZoom();
    const skip_message = multiselect_command.data.skip;
    if (skip_message) {
      onPressSkipMultiselect?.(skip_message, index);
    }
  };

  // Defaults to true
  const showEmotionHelp =
    `${command?.data?.show_emotion_help}`.toLowerCase() !== 'false';

  return (
    <Container>
      {text ? (
        <TextBlockRow>
          <TextBlock>
            <Text>{item.data.text}</Text>
          </TextBlock>
        </TextBlockRow>
      ) : null}

      <BottomHalf>
        <OptionsContainer>
          {options?.map((option, idx) => (
            <Option key={idx} option={option} onPress={onPressOption} />
          ))}
        </OptionsContainer>

        {image_url ? (
          <RightSideImageContainer onPress={showImageZoom}>
            <RightSideImage source={{ uri: image_url }} resizeMode="contain" />
            {/* <ZoomButton variant="secondary" icon="maximize" /> */}
          </RightSideImageContainer>
        ) : null}
      </BottomHalf>

      {multiselect_command && !item.data.disabled ? (
        <MultiselectActions>
          {multiselect_command.data.next ? (
            <Button size="large" onPress={onDone}>
              Done
            </Button>
          ) : null}
          {multiselect_command.data.skip ? (
            <Button size="large" variant="secondary" onPress={onSkip}>
              Skip
            </Button>
          ) : null}
        </MultiselectActions>
      ) : null}

      {image_zoomed && options && !item.data.disabled ? (
        <Portal hostName="focused-chat-portal">
          <EmotionPickerWindow>
            <EmotionPickerContainer>
              <FocusedPixiMessageContainer>
                <ChatItemTextBlock>
                  <Text>{item.data.text}</Text>
                </ChatItemTextBlock>
              </FocusedPixiMessageContainer>

              <EmotionPickerWheel
                other_emotion_label={command.data.other_emotion_label}
                other_emotion_input={command.data.other_emotion_input}
                options={options}
                selections={selections}
                onPressOption={onPressOption}
                onPressOtherEmotion={onPressOtherOption}
              />

              <TopRightButtonsContainer>
                {showEmotionHelp ? <EmotionPickerHelpButton /> : null}

                <CloseButton
                  onPress={hideImageZoom}
                  icon="times"
                  variant="secondary"
                />
              </TopRightButtonsContainer>

              {multiselect_command && !item.data.disabled ? (
                <FocusedMultiselectActions>
                  {multiselect_command.data.next ? (
                    <Button size="large" onPress={onDone}>
                      Done
                    </Button>
                  ) : null}
                  {multiselect_command.data.skip ? (
                    <Button size="large" variant="secondary" onPress={onSkip}>
                      Skip
                    </Button>
                  ) : null}
                </FocusedMultiselectActions>
              ) : null}
            </EmotionPickerContainer>
          </EmotionPickerWindow>
        </Portal>
      ) : null}
    </Container>
  );
}

function Option(props: {
  option: ChatResponseOption;
  onPress?: (option: ChatResponseOption) => void;
}) {
  const { option } = props;

  function onPress() {
    props.onPress?.(option);
  }

  return (
    <OptionContainer
      selected={option.selected}
      color={option.color}
      onPress={onPress}
    >
      <Text>{option.label}</Text>
    </OptionContainer>
  );
}

const TextBlockRow = styled.View`
  flex-direction: row;
`;

const Container = styled.View`
  gap: 12px;
`;

const TextBlock = styled.View`
  background-color: ${colors.grey[200]};
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  border-bottom-right-radius: 20px;
  padding: 16px;
`;

const BottomHalf = styled.View`
  flex-direction: row;
  gap: 10px;
`;

const OptionsContainer = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  flex: 1;
`;

const RightSideImage = styled.Image`
  width: 100%;
  height: 300px;
`;

const RightSideImageContainer = styled.TouchableOpacity`
  flex: 1;
`;

const FocusedPixiMessageContainer = styled.View`
  position: absolute;
  left: 20px;
  top: 20px;

  height: 100%;
  max-width: 200px;
`;

const EmotionPickerWindow = styled.View`
  position: absolute;
  top: 20px;
  bottom: 20px;
  left: 20px;
  right: 20px;
  border-radius: 20px;

  background-color: white;
`;

const EmotionPickerContainer = styled.View`
  position: relative;
  flex: 1;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: row;
`;

const TopRightButtonsContainer = styled(View)`
  position: absolute;
  top: 20px;
  right: 20px;
  flex-direction: row;
  gap: 10px;
`;

const CloseButton = styled(Button)``;

const MultiselectActions = styled.View`
  flex-direction: row;
  gap: 10px;
`;

const FocusedMultiselectActions = styled.View`
  position: absolute;
  bottom: 0;
  right: 0;
  margin: 20px;
  flex-direction: row;
  gap: 10px;
`;
